import React, { useCallback, useState } from 'react';

import { Alert, Box, Button, Stack, TextField } from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { RuleCriteria } from '~/services/AccommodationService';

import RuleCriteriaEditor, { hasAnyCriteria } from './RuleCriteriaEditor';

export interface RuleFormValues {
  name: string;
  description: string | null;
  criteria: RuleCriteria;
}

interface Props {
  // null represents an unsaved draft rule.
  initialValues: RuleFormValues | null;
  onSave: (values: RuleFormValues) => Promise<void>;
  // Omit to hide the delete action, e.g. while creating a new rule.
  onDelete?: () => Promise<void>;
  onCancel: () => void;
}

// Domain-agnostic add/edit form for a `Rule` — the caller owns how values are persisted (create
// vs. update vs. whatever "ruleable" entity it's attached to), passed in via onSave/onDelete.
function RuleForm({ initialValues, onSave, onDelete, onCancel }: Props) {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const [criteria, setCriteria] = useState<RuleCriteria>(initialValues?.criteria ?? {});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const showConfirmDialog = useConfirmDialog();

  const handleSave = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setErrorMessage(null);
      setIsSaving(true);
      try {
        await onSave({ name, description: description.trim() || null, criteria });
      } catch (err: unknown) {
        setErrorMessage(err instanceof Error ? err.message : 'Failed to save rule');
      } finally {
        setIsSaving(false);
      }
    },
    [name, description, criteria, onSave],
  );

  const handleDelete = useCallback(async () => {
    if (!onDelete) {
      return;
    }
    const confirmed = await showConfirmDialog({
      title: 'Delete rule',
      description: `Delete rule "${name}"? This cannot be undone.`,
    });
    if (!confirmed) {
      return;
    }
    setErrorMessage(null);
    setIsDeleting(true);
    try {
      await onDelete();
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to delete rule');
    } finally {
      setIsDeleting(false);
    }
  }, [name, onDelete, showConfirmDialog]);

  const canSubmit = !!name.trim() && hasAnyCriteria(criteria);

  return (
    <Box component="form" onSubmit={handleSave}>
      <Stack spacing={2}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth autoFocus />

        <TextField
          label="Description"
          helperText="Free-text notes about this rule"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />

        <RuleCriteriaEditor value={criteria} onChange={setCriteria} />

        <Box display="flex" justifyContent="space-between">
          <Box>
            {onDelete && (
              <Button color="error" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? 'Deleting…' : 'Delete rule'}
              </Button>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button type="button" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!canSubmit || isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

export default RuleForm;
